import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Box } from '@material-ui/core';
import { useStyles } from './styles';
import { EventType, IBooking } from '../../../types';
import { useAppConfiguration } from '../../shared/hook';
import { ModalWithConfirmation } from '../../shared/modal/ModalWithConfirmation';
import ChipInfo from '../../shared/chip-info';
import Card from '../../shared/card';
import EventCardBanner from '../../shared/event-card-banner';
import EventCardTitle from '../../shared/event-card-title';
import { getEventDate } from '../../../utils/dateUtils';
import MentorAvatar from '../../shared/mentor-avatar';
import { cancelBooking, getBookings } from '../../../services';
import poll from '../../../utils/poll';
import { addToastInfo } from '../../../store/common';
import { getEventLabel } from '../../../utils/event-type';

const EventCard = (props: { event: IBooking }) => {
  const { event } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { refetch: refetchMyBookings } = useQuery(['my-bookings'], getBookings);

  const { mutate: cancelWebinar } = useMutation(cancelBooking, {
    onSuccess: () => {
      poll({ fn: refetchMyBookings });
      setShowCancelModal(false);
      dispatch(
        addToastInfo({
          toastInfo: {
            open: true,
            type: 'warning',
            content: t('v2.global.snackbar_label_cancelledAttendance'),
          },
        }),
      );
    },
  });
  const { withUnlimitedCredits } = useAppConfiguration();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [isEventStartingBefore24Hr, setIsEventStartingBefore24Hr] = useState(
    false,
  );
  useEffect(() => {
    const timeDifference = moment(event.startsAt).diff(moment(), 'minutes');
    if (timeDifference < 1440) setIsEventStartingBefore24Hr(true);
  }, [event.startsAt]);

  const handleShowCancelModal = () => {
    setShowCancelModal(true);
  };

  const eventDate = getEventDate(event.startsAt);

  const handleCancelEvent = () => {
    if (event.type === EventType.WEBINAR) {
      return cancelWebinar({ bookingId: event.bookingId });
    }

    window.open(event.cancelUrl, '_blank');
    setShowCancelModal(false);
  };

  const handleJoinEvent = () => window.open(event.joinUrl, '_blank');

  const joinEventProps = event.joinUrl && {
    onCtaClick: handleJoinEvent,
  };

  return (
    <>
      <Card
        id={event?.offeringId}
        type="booking"
        header={
          <EventCardBanner
            offeringType={event.type}
            eventTypeLabel={getEventLabel(event.type)}
          />
        }
        content={
          <Box className={classes.cardContent}>
            <EventCardTitle
              date={eventDate}
              duration={event.duration}
              name={event.name}
            />
            <MentorAvatar
              imageSrc={event?.mentor?.profileImage}
              name={event?.mentor?.name}
            />
          </Box>
        }
        ctaLabel={t(
          'v2.myBookings.bookedEventCard_button_label_joinSessionViaZoom',
        )}
        {...joinEventProps}
        cancelLabel={t(
          'v2.global.button_label_upcomingEventCard_cancelAttendance',
        )}
        onCancel={handleShowCancelModal}
      />

      <ModalWithConfirmation
        onClose={() => setShowCancelModal(false)}
        isOpen={showCancelModal}
        title={t('v2.global.modal_cancelAttendance_label_title')}
        text={
          isEventStartingBefore24Hr && !withUnlimitedCredits
            ? t('v2.global.modal_cancelAttendance_moreThan24hrs_label_body')
            : t('v2.global.modal_cancelAttendance_lessThan24hrs_label_body')
        }
        snackbar={
          isEventStartingBefore24Hr &&
          !withUnlimitedCredits && (
            <Box>
              <ChipInfo
                type="warning"
                text={t(
                  'v2.global.modal_cancelAttendance_lessThan24hrs_label_banner',
                )}
              />
            </Box>
          )
        }
        onCancel={() => setShowCancelModal(false)}
        onConfirm={handleCancelEvent}
        cancelBtnlabel={t('v2.global.button_label_goBack')}
        confirmBtnLabel={t(
          'v2.global.button_label_upcomingEventCard_cancelAttendance',
        )}
      />
    </>
  );
};

export default EventCard;
